/**
 * Funnel-truth F2 (FT-AC-090): the ONE place that maps the selected counting
 * unit to the served fields of a node/edge metric. A label swap without the
 * field swap is the program's named failure — components render THIS view,
 * never pick per-unit fields ad hoc. React-free so jest pins label↔field
 * pairs directly.
 */

import type {
  FunnelHeatBucket,
  FunnelInsightEdgeMetric,
  FunnelInsightNodeMetric,
  FunnelReportsScopeRollup,
  MetricQualification,
} from './types';
import type { FunnelUnit } from './funnelUnit';

export interface UnitNodeFlowView {
  /**
   * false ⇔ people mode on a payload without the F1 person twins (old cache,
   * fixture branch) — render the D7 typed-absence line, never a zero and
   * never the journey number relabelled (FT-AC-087).
   */
  available: boolean;
  visitors?: number;
  /** Explicit FunnelExited counter at the selected grain. */
  explicitExits?: number;
  /** The honest total loss the hero leads with: max(explicit, flow delta). */
  lossCount?: number;
  lossRate?: number;
  cumulativeReach?: number;
  heatBucket?: FunnelHeatBucket;
  qualification?: MetricQualification;
}

/**
 * Per-unit node flow view. The visits branch reproduces the pre-F2
 * derivations verbatim (explicit-vs-flow-loss max, rate source selection) so
 * the default render stays byte-identical; the people branch reads ONLY the
 * `*People` twins — the same formulas server-side, per-grain (AC-D1-7).
 */
export function nodeFlowForUnit(metric: FunnelInsightNodeMetric, unit: FunnelUnit): UnitNodeFlowView {
  if (unit === 'people') {
    if (typeof metric.visitorsPeople !== 'number') {
      return { available: false };
    }
    const explicit = metric.exitsPeople ?? 0;
    const flowLoss = metric.flowLossCountPeople ?? 0;
    return {
      available: true,
      visitors: metric.visitorsPeople,
      explicitExits: explicit,
      lossCount: Math.max(explicit, flowLoss),
      lossRate: flowLoss > explicit ? metric.flowLossRatePeople : metric.dropOffRatePeople,
      cumulativeReach: metric.cumulativeReachPeople,
      heatBucket: metric.heatBucketPeople,
      qualification: metric.qualificationPeople,
    };
  }
  const explicit = metric.dropOffCount ?? 0;
  const flowLoss = metric.flowLossCount ?? 0;
  return {
    available: true,
    visitors: metric.visitors,
    explicitExits: explicit,
    lossCount: Math.max(explicit, flowLoss),
    lossRate: flowLoss > explicit ? metric.flowLossRate : metric.dropOffRate,
    cumulativeReach: metric.cumulativeReach,
    heatBucket: metric.heatBucket,
    qualification: metric.qualification,
  };
}

export interface UnitEdgeFlowView {
  /** false ⇔ people mode on an edge without the F1 person twin. */
  available: boolean;
  count?: number;
  /** Share of the source screen's per-unit visitors (server-computed). */
  share?: number;
}

export function edgeFlowForUnit(metric: FunnelInsightEdgeMetric, unit: FunnelUnit): UnitEdgeFlowView {
  if (unit === 'people') {
    if (typeof metric.uniquePeople !== 'number') {
      return { available: false };
    }
    return { available: true, count: metric.uniquePeople, share: metric.sourceShareRatePeople };
  }
  return { available: true, count: metric.uniqueJourneys, share: metric.sourceShareRate };
}

/** The three PRIMARY leaderboard gates that carry a person twin (D-U1). */
export type PlacementGateId = 'firstToSecond' | 'paywallReach' | 'purchaseConv';

export interface UnitGateRateView {
  /**
   * false ⇔ people mode on a row whose person twins never served — an alias
   * with no person rows at all (`attachPersonScopeRollups` attaches nothing),
   * a version-scoped row (FT-AC-071), or a pre-P payload. Render the typed
   * absence; a zero here would read as «nobody converted» (FT-AC-121).
   */
  available: boolean;
  rate?: number | null;
  numerator?: number;
  denominator?: number;
  qualification?: MetricQualification;
}

/**
 * D-U1 (FT-AC-117/120): the ONE mapping from counting unit to the fields of a
 * leaderboard rate cell. The cell AND the column's sortValue both read it, so
 * a switched column can never be sorted by the other unit's number — the
 * failure mode that made the pre-D-U1 table show people in Starts and journeys
 * in the three rates beside it.
 *
 * Only ALIAS rows can answer in people. Version rows and A/B variant sub-rows
 * are journey-only by contract (FT-AC-071: one person can use several
 * versions, so per-version people would double-count) and are labelled
 * «· visits» in BOTH modes rather than routed through here.
 */
export function placementGateForUnit(
  row: FunnelReportsScopeRollup,
  gate: PlacementGateId,
  unit: FunnelUnit,
): UnitGateRateView {
  if (unit === 'people') {
    const people = gate === 'firstToSecond'
      ? {
        rate: row.firstToSecondRatePeople,
        numerator: row.firstToSecondPeople,
        denominator: row.firstScreenOpenedPeople,
        qualification: row.qualification?.firstToSecondRatePeople,
      }
      : gate === 'paywallReach'
        ? {
          rate: row.paywallReachRatePeople,
          numerator: row.paywallReachedPeople,
          denominator: row.startedPeople,
          qualification: row.qualification?.paywallReachRatePeople,
        }
        : {
          rate: row.purchaseConversionPeople,
          numerator: row.purchasePeople,
          denominator: row.startedPeople,
          qualification: row.qualification?.purchaseConversionPeople,
        };
    // A zero denominator is a VALUE (stage serves firstScreenOpenedPeople = 0
    // warehouse-wide) — it renders «no denominator», not absence. Only a
    // missing key is absence.
    if (typeof people.numerator !== 'number' || typeof people.denominator !== 'number') {
      return { available: false };
    }
    return {
      available: true,
      rate: people.rate ?? null,
      numerator: people.numerator,
      denominator: people.denominator,
      qualification: people.qualification,
    };
  }
  if (gate === 'firstToSecond') {
    return {
      available: true,
      rate: row.firstToSecondRate,
      numerator: row.firstToSecondJourneys,
      denominator: row.firstScreenOpenedJourneys,
      qualification: row.qualification?.firstToSecondRate,
    };
  }
  if (gate === 'paywallReach') {
    return {
      available: true,
      rate: row.paywallReachRate,
      numerator: row.paywallReachedJourneys,
      denominator: row.startedJourneys,
      qualification: row.qualification?.paywallReachRate,
    };
  }
  // Purchase conversion has no served journey rate — derived here verbatim as
  // it was before D-U1 so the visits branch stays byte-identical.
  return {
    available: true,
    rate: row.startedJourneys > 0 ? row.purchaseJourneys / row.startedJourneys : null,
    numerator: row.purchaseJourneys,
    denominator: row.startedJourneys,
    qualification: row.qualification?.purchaseConversion,
  };
}
